import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money, fmtDate, daysUntil } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  ArrowDownRight,
  ArrowUpRight,
  Sparkles,
  Loader2,
  RefreshCw,
  Package,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { toast } from "sonner";
import ChartBox from "@/components/ChartBox";
import { useT } from "@/lib/i18n";

const PIE_COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#F43F5E", "#8B5CF6", "#06B6D4", "#EC4899", "#84CC16"];

const StatCard = ({ label, value, icon: Icon, tone, sub, testid }) => (
  <div
    data-testid={testid}
    className="group rounded-xl border border-border bg-card p-6 transition-transform duration-200 hover:-translate-y-1"
  >
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${tone}`}>
        <Icon className="h-[18px] w-[18px]" />
      </div>
    </div>
    <div className="mt-4 font-mono text-3xl font-semibold tracking-tight tabular">{value}</div>
    {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-xl">
      <div className="mb-1 font-semibold">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2 font-mono">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="capitalize text-muted-foreground">{p.name}:</span>
          <span>{money(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [mamul, setMamul] = useState([]);
  const [ai, setAi] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const load = async () => {
    const [s, p, m] = await Promise.all([api.summary(), api.payments(), api.stock({ category: "mamul", limit: 50 })]);
    setData(s);
    setPayments(p.filter((x) => x.status === "pending").slice(0, 6));
    const rows = (m.items || []).filter((x) => (x.sell_amount || 0) > 0).sort((a, b) => (b.sell_amount || 0) - (a.sell_amount || 0));
    setMamul(rows);
  };

  useEffect(() => {
    load();
  }, []);

  const runAi = async () => {
    setAiLoading(true);
    setAi("");
    try {
      const res = await api.aiInsights();
      setAi(res.insight);
    } catch (e) {
      toast.error(t("dash.aiFail"));
    } finally {
      setAiLoading(false);
    }
  };

  if (!data) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-5 pt-20 lg:p-10 lg:pt-10">
      <PageHeader
        title={t("nav.dashboard")}
        subtitle={`${t("dash.period")}: ${data.month_label} · ${t("dash.overview")}`}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          testid="stat-total-balance"
          label={t("dash.netPosition")}
          value={money(data.total_balance)}
          icon={Wallet}
          tone="bg-primary/15 text-primary"
          sub={`${data.account_count} ${t("dash.bankAccounts")}`}
        />
        <StatCard
          testid="stat-month-income"
          label={`${t("dash.income")} · ${data.month_label}`}
          value={money(data.month_income)}
          icon={TrendingUp}
          tone="bg-emerald-500/15 text-emerald-400"
        />
        <StatCard
          testid="stat-month-expense"
          label={`${t("dash.expense")} · ${data.month_label}`}
          value={money(data.month_expense)}
          icon={TrendingDown}
          tone="bg-rose-500/15 text-rose-400"
        />
        <StatCard
          testid="stat-month-net"
          label={t("dash.netCashflow")}
          value={money(data.month_net)}
          icon={data.month_net >= 0 ? ArrowUpRight : ArrowDownRight}
          tone={data.month_net >= 0 ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}
        />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-12">
        {/* Cashflow chart */}
        <div className="rounded-xl border border-border bg-card p-6 lg:col-span-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-medium">{t("dash.monthlyCashflow")}</h3>
              <p className="text-xs text-muted-foreground">{t("dash.last6")}</p>
            </div>
          </div>
          <ChartBox height={280}>
            {(w, h) => (
            <AreaChart width={w} height={h} data={data.cashflow} margin={{ left: -10, right: 8 }}>
              <defs>
                <linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F43F5E" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#F43F5E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
              <XAxis dataKey="month" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="gelir" stroke="#10B981" strokeWidth={2} fill="url(#gInc)" isAnimationActive={false} />
              <Area type="monotone" dataKey="gider" stroke="#F43F5E" strokeWidth={2} fill="url(#gExp)" isAnimationActive={false} />
            </AreaChart>
            )}
          </ChartBox>
        </div>

        {/* Expense by category */}
        <div className="rounded-xl border border-border bg-card p-6 lg:col-span-4">
          <h3 className="font-display text-lg font-medium">{t("dash.expenseDist")}</h3>
          <p className="mb-4 text-xs text-muted-foreground">{t("dash.thisMonthCat")}</p>
          {data.expense_by_category.length === 0 ? (
            <div className="flex h-[240px] items-center justify-center text-sm text-muted-foreground">
              {t("dash.noExpense")}
            </div>
          ) : (
            <ChartBox height={240}>
              {(w, h) => (
              <PieChart width={w} height={h}>
                <Pie
                  data={data.expense_by_category}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={2}
                  isAnimationActive={false}
                >
                  {data.expense_by_category.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="none" />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11, color: "#94A3B8" }}
                />
              </PieChart>
              )}
            </ChartBox>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-12">
        {/* AI Insights */}
        <div className="ai-glow relative overflow-hidden rounded-xl border border-amber-500/30 bg-card p-6 lg:col-span-7">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/15 text-amber-400">
                <Sparkles className="h-[18px] w-[18px]" />
              </div>
              <div>
                <h3 className="font-display text-lg font-medium">{t("dash.aiTitle")}</h3>
                <p className="text-xs text-muted-foreground">{t("dash.aiSub")}</p>
              </div>
            </div>
            <Button
              data-testid="ai-analyze-button"
              onClick={runAi}
              disabled={aiLoading}
              size="sm"
              className="bg-amber-500 text-black hover:bg-amber-400"
            >
              {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-1.5">{t("dash.analyze")}</span>
            </Button>
          </div>
          <div data-testid="ai-insight-content" className="min-h-[120px] text-sm leading-relaxed text-slate-300">
            {aiLoading && !ai && <span className="text-muted-foreground">{t("dash.analyzing")}</span>}
            {!aiLoading && !ai && (
              <span className="text-muted-foreground">
                {t("dash.aiPrompt")}
              </span>
            )}
            {ai && <div className="whitespace-pre-wrap">{ai}</div>}
          </div>
        </div>

        {/* Upcoming payments */}
        <div className="rounded-xl border border-border bg-card p-6 lg:col-span-5">
          <h3 className="font-display text-lg font-medium">{t("dash.upcoming")}</h3>
          <p className="mb-4 text-xs text-muted-foreground">{data.upcoming_count} {t("dash.pending")}</p>
          <div className="space-y-2">
            {payments.length === 0 && (
              <div className="py-8 text-center text-sm text-muted-foreground">{t("dash.noPending")}</div>
            )}
            {payments.map((p) => {
              const d = daysUntil(p.due_date);
              const overdue = d < 0;
              const isColl = p.type === "collection";
              return (
                <div
                  key={p.id}
                  data-testid="dashboard-payment-item"
                  className="flex items-center justify-between rounded-lg border border-border/60 bg-secondary/30 px-3.5 py-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isColl ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>
                      {isColl ? <ArrowDownRight className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{p.title}</div>
                      <div className={`text-xs ${overdue ? "text-rose-400" : "text-muted-foreground"}`}>
                        {overdue ? `${Math.abs(d)} ${t("dash.daysLate")}` : d === 0 ? t("dash.today") : `${d} ${t("dash.daysLeft")}`}
                      </div>
                    </div>
                  </div>
                  <div className={`shrink-0 font-mono text-sm font-semibold tabular ${isColl ? "text-emerald-400" : "text-rose-400"}`}>
                    {isColl ? "+" : "-"}{money(p.amount)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mamül sales panel */}
      <div className="mt-4 rounded-xl border border-border bg-card p-6" data-testid="mamul-sales-panel">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-500/15 text-sky-400">
            <Package className="h-[18px] w-[18px]" />
          </div>
          <div>
            <h3 className="font-display text-lg font-medium">{t("dash.mamulTitle")}</h3>
            <p className="text-xs text-muted-foreground">{t("dash.mamulSub")}</p>
          </div>
        </div>
        {mamul.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">{t("dash.noMamul")}</div>
        ) : (
          <div className="space-y-2.5">
            {mamul.map((m) => {
              const max = mamul[0]?.sell_amount || 1;
              const pct = Math.max(4, Math.round(((m.sell_amount || 0) / max) * 100));
              return (
                <div key={m.id} data-testid="mamul-sales-row" className="flex items-center gap-3">
                  <div className="w-16 shrink-0 font-mono text-xs text-teal-300">{m.code || "-"}</div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <span className="truncate text-xs text-muted-foreground">{m.name}</span>
                      <span className="shrink-0 font-mono text-xs font-semibold">{money(m.sell_amount)}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-secondary">
                      <div className="h-full rounded-full bg-gradient-to-r from-sky-500 to-teal-400" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <div className="w-20 shrink-0 text-right font-mono text-xs text-sky-300">{(m.sell_qty || 0).toLocaleString("tr-TR")} {t("dash.unit")}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
